import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { usePermissions } from "@/hooks/use-permissions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SearchIcon,
  PlusIcon,
  PencilIcon,
  Trash2Icon,
  EyeIcon,
  RotateCcwIcon as RestoreIcon,
  CopyIcon,
} from "lucide-react";
import { ClientAvatar } from "@/components/client-avatar";
import { Pagination } from "@/components/ui/pagination";
import { useClientMutations } from "./useClientMutations";
import { ClientFormSheet } from "./ClientFormSheet";
import { toast } from "sonner";

const PAGE_SIZE = 25;

interface ClientsResponse {
  data: ClientRow[];
  total: number;
  page: number;
  pageSize: number;
}

interface ClientRow {
  id: string;
  code: string;
  fileId: string | null;
  name: string;
  sgtCodes: string[] | null;
  docId: string | null;
  docTypeId: string | null;
  docTypeName: string | null;
  address: string | null;
  cityName: string | null;
  postalCode: string | null;
  stateId: string | null;
  stateName: string | null;
  countryId: string | null;
  countryName: string | null;
  avatarUrl: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

type FilterStatus = "active" | "deleted" | "all";

interface Props {
  defaultSearch?: string;
}

export function ClientsTable({ defaultSearch = "" }: Props) {
  const { can } = usePermissions();
  const navigate = useNavigate();

  const [searchInput, setSearchInput]   = useState(defaultSearch);
  const [search, setSearch]             = useState(defaultSearch);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("active");
  const [page, setPage]                 = useState(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sheetOpen, setSheetOpen]       = useState(false);
  const [deleteOpen, setDeleteOpen]     = useState(false);
  const [editing, setEditing]           = useState<ClientRow | null>(null);
  const [deleting, setDeleting]         = useState<ClientRow | null>(null);
  const [formError, setFormError]       = useState<string | null>(null);
  const [filterSgt, setFilterSgt]       = useState<"all" | "with" | "without">("all");

  useEffect(() => {
    if (defaultSearch) {
      setSearchInput(defaultSearch);
      setSearch(defaultSearch);
      setPage(1);
    }
  }, [defaultSearch]);

  function handleSearchChange(value: string) {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 350);
  }

  const { data: response, isLoading } = useQuery<ClientsResponse>({
    queryKey: ["clients", { status: filterStatus, search, sgt: filterSgt, page }],
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const query: Record<string, string> = { page: String(page), pageSize: String(PAGE_SIZE) };
      if (filterStatus === "active")  query.deleted = "false";
      if (filterStatus === "deleted") query.deleted = "true";
      if (search.trim())              query.search  = search.trim();
      if (filterSgt !== "all")        query.sgt     = filterSgt;

      const res = await api.clients.$get({ query } as any);
      return res.json() as Promise<ClientsResponse>;
    },
  });

  const paged      = response?.data ?? [];
  const total      = response?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const { createMut, updateMut, deleteMut, restoreMut, updateAvatarMut, deleteAvatarMut, isSaving } = useClientMutations({
    onSuccess: () => {
      setSheetOpen(false);
      setDeleteOpen(false);
      setEditing(null);
      setDeleting(null);
      setFormError(null);
    },
    onError: (err) => setFormError(err.message),
  });

  function openCreate() {
    setEditing(null);
    setFormError(null);
    setSheetOpen(true);
  }

  function openEdit(item: ClientRow) {
    setEditing(item);
    setFormError(null);
    setSheetOpen(true);
  }

  function handleSave(id: string | null, data: any) {
    if (id) {
      updateMut.mutate({ id, data });
    } else {
      createMut.mutate(data);
    }
  }

  return (
    <div className="space-y-4 w-full">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-64">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o código..."
              className="pl-8"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>

          <Select value={filterSgt} onValueChange={(v: any) => { setFilterSgt(v); setPage(1); }}>
            <SelectTrigger className="w-[140px]">
              <SelectValue>
                {filterSgt === "all" ? "Todos (SGT)" : filterSgt === "with" ? "Con SGT" : "Sin SGT"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos (SGT)</SelectItem>
              <SelectItem value="with">Con SGT</SelectItem>
              <SelectItem value="without">Sin SGT</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={(v: any) => { setFilterStatus(v); setPage(1); }}>
            <SelectTrigger className="w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">activos</SelectItem>
              <SelectItem value="deleted">borrados</SelectItem>
              <SelectItem value="all">todos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {can("write", "clients") && (
          <Button onClick={openCreate} className="gap-2">
            <PlusIcon className="size-4" /> Nuevo cliente
          </Button>
        )}
      </div>

      <div className="rounded-md border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Legajo</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Localidad</TableHead>
              <TableHead>SGT Codes</TableHead>
              <TableHead className="w-[120px] text-right pr-4">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  Cargando clientes...
                </TableCell>
              </TableRow>
            ) : paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  No se encontraron clientes.
                </TableCell>
              </TableRow>
            ) : (
              paged.map((item) => (
                <TableRow key={item.id} className={item.deletedAt ? "opacity-50 italic" : ""}>
                  <TableCell className="font-mono font-medium text-xs">
                    {item.fileId ? (
                      <span className="bg-muted px-1.5 py-0.5 rounded">{item.fileId}</span>
                    ) : <span className="text-muted-foreground/30">—</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <ClientAvatar clientId={item.id} clientName={item.name} className="size-7" />
                      <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-muted-foreground font-mono">{item.code}</span>
                          <button
                            type="button"
                            title="Copiar ID"
                            onClick={() => {
                              navigator.clipboard.writeText(item.id)
                              toast.success("ID copiado", { description: item.id, duration: 2000 })
                            }}
                            className="text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                          >
                            <CopyIcon className="size-2.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs text-muted-foreground uppercase">{item.cityName || "—"}</div>
                    <div className="text-[10px] text-muted-foreground/60">{item.countryName || "—"}</div>
                  </TableCell>
                  <TableCell>
                    {item.sgtCodes && item.sgtCodes.length > 0 ? (
                      <div className="flex flex-wrap gap-1 max-w-[300px]">
                        {item.sgtCodes.map((c) => (
                          <span key={c} className="px-1.5 py-0.5 rounded-sm bg-muted font-mono text-[10px] text-muted-foreground">
                            {c}
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-muted-foreground/40 text-xs">—</span>}
                  </TableCell>
                  <TableCell className="text-right pr-4">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => navigate(`/clients/${item.id}`)}
                        title="Ver detalle"
                      >
                        <EyeIcon className="size-3.5" />
                      </Button>

                      {can("write", "clients") && (
                        item.deletedAt ? (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => restoreMut.mutate(item.id)}
                            disabled={restoreMut.isPending}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Restaurar"
                          >
                            <RestoreIcon className="size-3.5" />
                          </Button>
                        ) : (
                          <>
                            <Button variant="ghost" size="icon-sm" onClick={() => openEdit(item)} title="Editar">
                              <PencilIcon className="size-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => { setDeleting(item); setDeleteOpen(true); }}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              title="Eliminar"
                            >
                              <Trash2Icon className="size-3.5" />
                            </Button>
                          </>
                        )
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      </div>

      <ClientFormSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        editing={editing}
        onSave={handleSave}
        isSaving={isSaving}
        error={formError}
        onAvatarUpload={(id: string, file: File) => updateAvatarMut.mutate({ id, file })}
        onAvatarDelete={(id: string) => deleteAvatarMut.mutate(id)}
        isAvatarSaving={updateAvatarMut.isPending || deleteAvatarMut.isPending}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar cliente</DialogTitle>
            <DialogDescription>
              ¿Eliminás el cliente <strong>{deleting?.name}</strong>? Esta acción es reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
